import React, { useState, useEffect } from 'react'
import { Tabs, Tab, Button } from 'react-bootstrap'
import Title from 'common/components/Title'
import ExpectedMovementsList from 'application/budget/plan/ExpectedMovementsList'
import MonthlyOutlook from 'application/budget/plan/MonthlyOutlook'
import MonthOfYear from 'common/logic/MonthOfYear'
import API from 'common/logic/API'
import BigSpinner from 'common/components/BigSpinner'
import * as Icons from 'react-bootstrap-icons'
import CreatePlanWorkflow from 'application/budget/plan/CreatePlanWorkflow'
import PlanSelectionModal from 'application/budget/PlanSelectionModal'
import Utils from 'common/Utils'
import { BudgetPlanSummary } from 'types'

interface PlanData extends BudgetPlanSummary {
    validTo: string
}

interface Props {
    query: Record<string, string>
    url: string
}

function Plan(_props: Props) {
    const [currentPlan, setCurrentPlan] = useState<PlanData | null>(null)
    const [plans, setPlans] = useState<PlanData[] | null>(null)
    const [modal, setModal] = useState<{ type: string } | null>(null)

    const calculateMonth = () => {
        const query = new URLSearchParams(window.location.search)
        const fromParam = query.get("month") || ""
        return MonthOfYear.parse(fromParam) || null
    }

    useEffect(() => {
        const month = calculateMonth()
        API.multiple({
            currentPlan: API.get(`/budget-plan/current`),
            plans: API.get(`/budget-plan`),
        }).then((response: any) => {
            const plansData: PlanData[] = response.plans.results.map((p: any, i: number, arr: any[]) => {
                const next = i + 1 < arr.length ? arr[i + 1] : null
                return {
                    id: p.id,
                    validFrom: p.validFrom,
                    validTo: next ? next.validFrom : "current"
                }
            })
            let plan: PlanData | undefined
            if (month) {
                const _month = month.toString()
                plan = plansData.find(p => p.validFrom.toString() === _month)
            } else {
                plan = plansData.find(p => p.id === response.currentPlan.id)
            }
            setCurrentPlan(plan || null)
            setPlans(plansData)
        }).catch((error: any) => {
            console.error(error.message)
        })
    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    const showPlanSelection = () => setModal({ type: "selectPlan" })
    const showPlanCreation = () => setModal({ type: "create" })
    const hideDialogs = () => setModal(null)

    if (!currentPlan) {
        return <>
            <Title title="Monthly budget plan" />
            <BigSpinner />
        </>
    }

    let modalElement: React.ReactNode = null
    if (modal) {
        switch (modal.type) {
            case "selectPlan":
                modalElement = <PlanSelectionModal
                    currentPlan={currentPlan}
                    plans={plans!}
                    onHide={hideDialogs}
                    onCreate={showPlanCreation}
                    onSelect={(plan) => {
                        hideDialogs()
                        setCurrentPlan(plan as PlanData)
                        Utils.pushUrlWithParams({ month: plan.validFrom.toString() })
                    }}
                />
                break
            case "create":
                modalElement = <CreatePlanWorkflow
                    onSuccess={() => {
                        hideDialogs()
                    }}
                />
                break
            default:
        }
    }

    return <>
        <Title title="Monthly budget plan" />
        <div>Valid from {currentPlan.validFrom} to {currentPlan.validTo}
        <Button variant="link" onClick={showPlanSelection}><Icons.BoxArrowInUpRight /></Button></div>
        {modalElement}
        <Tabs mountOnEnter={true} unmountOnExit={true}>
            <Tab eventKey="summary" title="Monthly outlook">
                <MonthlyOutlook currentPlan={currentPlan} />
            </Tab>
            <Tab eventKey="movements" title="All expected movements">
                <ExpectedMovementsList currentPlan={currentPlan} />
            </Tab>
        </Tabs>
    </>
}

export default Plan
